import { useCallback, useState } from 'react';
import { StanBanner, StanButton } from '@/components';
import { formatCurrency } from '@/lib/currency';
import { createIconElement, PRODUCT_TYPE_LABELS } from '@/lib';
import { stripHtmlTags } from '@/lib/helpers';
import { configSettings } from '@/lib/config';
import IcImage from '@/assets/icons/ic-image.svg';
import IcInfo from '@/assets/icons/ic-info-circle.svg';
import { DeleteFunnelPageModal } from '../modals/DeleteFunnelPageModal';
import type { FunnelPageItem } from '../../../types';
import { getFunnelPageIcon } from '../../../logic';
import { RemovePageButton } from './RemovePageButton';

interface LegacyFunnelPageCardProps {
    page: FunnelPageItem;
    stepNumber: number;
    storeId: number;
    isDeleting: boolean;
    onDelete: (pageId: number) => void;
    onReplace: () => void;
}

export const LegacyFunnelPageCard = ({
    page,
    stepNumber,
    storeId,
    isDeleting,
    onDelete,
    onReplace,
}: LegacyFunnelPageCardProps) => {
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const typeIcon = createIconElement(getFunnelPageIcon(page.type), { className: 'size-4 shrink-0' });
    const typeLabel = PRODUCT_TYPE_LABELS[page.type as keyof typeof PRODUCT_TYPE_LABELS] ?? null;
    const priceLabel = typeof page.price === 'number' ? formatCurrency(page.price, page.currency ?? 'USD') : null;
    const description = page.description ? stripHtmlTags(page.description) : '';

    const handleEditLegacy = useCallback(() => {
        const legacyDomain = configSettings.legacyAdminDomain?.replace(/\/+$/, '');
        if (!legacyDomain) return;
        // Vue's funnel_edit_page route uses the page type as the :slug segment (see Funnel.vue
        // `editPage(page.page_id, page.type, i)`), with `order` as the 0-based step index.
        const order = Math.max(0, stepNumber - 1);
        window.location.href = `${legacyDomain}/funnels/${storeId}/page/${page.type}/${page.page_id}?order=${order}`;
    }, [storeId, page.type, page.page_id, stepNumber]);

    const confirmDelete = () => {
        onDelete(page.page_id);
        setShowDeleteModal(false);
    };

    return (
        <>
            <div className='bg-canvas shadow-stan animate-in fade-in slide-in-from-bottom-1 flex flex-1 flex-col gap-4 rounded-2xl p-4 duration-200 ease-out md:gap-6 md:p-7'>
                <StanBanner
                    variant='info'
                    icon={IcInfo}
                    title='Legacy product page'
                    subtitle='Edit this page in the legacy editor, or replace it with an existing store product to configure V2 options like upsell and downsell routing.'
                />

                <div className='flex items-start justify-between gap-4'>
                    <div className='flex flex-col gap-2'>
                        <h2 className='text-heading-h3 text-dark md:text-heading-h2'>
                            {page.title || `Page ${stepNumber}`}
                        </h2>
                        <p className='text-body-sm text-gray md:text-body-md'>Legacy product attached.</p>
                    </div>

                    <RemovePageButton onClick={() => setShowDeleteModal(true)} />
                </div>

                <div className='border-neutral-subtle flex items-start gap-3 rounded-md border p-3 md:gap-4 md:p-4'>
                    <div className='border-neutral-subtle flex size-14 shrink-0 items-center justify-center overflow-hidden rounded-md border md:size-16'>
                        {page.image ? (
                            <img src={page.image} alt='' className='size-full object-cover' />
                        ) : (
                            <IcImage className='text-light size-6' />
                        )}
                    </div>
                    <div className='flex min-w-0 flex-1 flex-col gap-1.5'>
                        <div className='flex items-start justify-between gap-3'>
                            <span className='text-heading-h4 text-dark truncate'>
                                {page.title || 'Untitled Product'}
                            </span>
                            {priceLabel && (
                                <span className='text-body-sm text-dark shrink-0 font-semibold'>{priceLabel}</span>
                            )}
                        </div>
                        {typeLabel && (
                            <span className='text-body-sm text-gray flex items-center gap-1.5'>
                                {typeIcon}
                                {typeLabel}
                            </span>
                        )}
                        {description && <p className='text-body-sm text-gray line-clamp-2'>{description}</p>}
                    </div>
                </div>

                <div className='flex flex-col-reverse items-stretch gap-1.5 md:flex-row md:flex-wrap md:items-center md:justify-end md:gap-3'>
                    <StanButton
                        name='edit-legacy-funnel-page'
                        variant='outline'
                        onClick={handleEditLegacy}
                        className='w-full md:w-auto'
                    >
                        Edit in Legacy Editor
                    </StanButton>
                    <StanButton name='replace-legacy-funnel-page' onClick={onReplace} className='w-full md:w-auto'>
                        Replace With Existing Product
                    </StanButton>
                </div>
            </div>

            <DeleteFunnelPageModal
                isOpen={showDeleteModal}
                onClose={() => setShowDeleteModal(false)}
                pageTitle={page.title || `Page ${stepNumber}`}
                isDeleting={isDeleting}
                onConfirm={confirmDelete}
            />
        </>
    );
};
